import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import type { PopoverOrigin } from "@mui/material";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import AccountBalanceWalletRounded from "@mui/icons-material/AccountBalanceWalletRounded";
import SavingsRounded from "@mui/icons-material/SavingsRounded";
import AccountBalanceRounded from "@mui/icons-material/AccountBalanceRounded";
import SmartToyRounded from "@mui/icons-material/SmartToyRounded";
import PlaylistAddRounded from "@mui/icons-material/PlaylistAddRounded";
import FilterAltRounded from "@mui/icons-material/FilterAltRounded";
import { useNavigate } from "react-router-dom";
import type { SvgIconComponent } from "@mui/icons-material";
import { mobileFullWidthMenuPaperSx } from "@/theme/mobile-menu-sx";

/**
 * Shared Create menu — the "new X" shortcut list that hangs off every (+)
 * button in the app (sidebar CreateButton, AccountsPage header + icon,
 * and the mobile Accounts FAB). Keep the action list in one place so
 * adding/reordering actions doesn't require hunting down multiple menus.
 *
 * Order is deliberate: transactional actions first (Order / Add money /
 * Withdraw), then setup actions (Broker, Bot, List, Screener).
 */

export interface CreateAction {
  label: string;
  description: string;
  icon: SvgIconComponent;
  onClick: (navigate: ReturnType<typeof useNavigate>) => void;
}

export const CREATE_ACTIONS: CreateAction[] = [
  {
    label: "Order",
    description: "Place a Manual or Smart Buy/Sell Order.",
    icon: ReceiptLongRounded,
    onClick: (nav) => nav("/accounts")
  },
  {
    label: "Add money",
    description: "Deposit cash into your brokerage account.",
    icon: AccountBalanceWalletRounded,
    onClick: (nav) => nav("/accounts")
  },
  {
    label: "Withdraw",
    description: "Transfer cash out of your brokerage account.",
    icon: SavingsRounded,
    onClick: (nav) => nav("/accounts")
  },
  {
    label: "Broker / Paper Account",
    description: "Connect a broker (live or paper) to run bots against it.",
    icon: AccountBalanceRounded,
    onClick: (nav) => nav("/accounts")
  },
  {
    label: "New Bot",
    description: "Build a new trading bot in the Algo Builder.",
    icon: SmartToyRounded,
    onClick: (nav) => nav("/algo")
  },
  {
    label: "New List",
    description: "Create a watchlist of tickers for your bots.",
    icon: PlaylistAddRounded,
    onClick: (nav) => nav("/lists")
  },
  {
    label: "Custom Screener",
    description: "Build a fundamental + technical screener over the universe.",
    icon: FilterAltRounded,
    onClick: (nav) => nav("/lists")
  }
];

/**
 * Bot-context actions. Shown from the (+) on BotDetailPage. Only
 * Add money + Withdraw — in this scope they mean *proportional rebalance
 * in / out* of the bot's holdings (partial bot
 * buy/sell model), not plain cash transfers. The copy reflects that.
 */
export const BOT_CREATE_ACTIONS: CreateAction[] = [
  {
    label: "Add money",
    description: "Deposit cash into this bot — proportional buy across all holdings.",
    icon: AccountBalanceWalletRounded,
    onClick: () => { /* TODO: open bot deposit → proportional rebalance-in flow */ }
  },
  {
    label: "Withdraw",
    description: "Withdraw cash from this bot — proportional sell across all holdings.",
    icon: SavingsRounded,
    onClick: () => { /* TODO: open bot withdraw → proportional rebalance-out flow */ }
  }
];

interface CreateMenuProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Override default below-anchor placement (used by sidebar + FAB). */
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  /** Action list. Defaults to the full `CREATE_ACTIONS` (account context). */
  actions?: CreateAction[];
}

export function CreateMenu({ anchor, onClose, anchorOrigin, transformOrigin, actions = CREATE_ACTIONS }: CreateMenuProps) {
  const navigate = useNavigate();
  return (
    <Menu
      open={!!anchor}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          sx: [
            { borderRadius: 3, mt: 0.5, minWidth: 280 },
            mobileFullWidthMenuPaperSx
          ]
        }
      }}
    >
      {actions.map(({ label, description, icon: Icon, onClick }) => (
        <MenuItem
          key={label}
          onClick={() => { onClose(); onClick(navigate); }}
          sx={{ py: 1.25, px: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
            <Icon />
          </ListItemIcon>
          <ListItemText
            primary={label}
            secondary={description}
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: "0.75rem" }}
          />
        </MenuItem>
      ))}
    </Menu>
  );
}
